import React, { useState, useCallback } from 'react'
import useTransactionDeadline from '../../hooks/useTransactionDeadline'
import Modal from '../Modal'
import { AutoColumn } from '../Column'
import styled from 'styled-components'
import { RowBetween } from '../Row'
import { TYPE, CloseIcon } from '../../theme'
import { ButtonConfirmed, ButtonError } from '../Button'
import CurrencyInputPanel from '../CurrencyInputPanel'
import { CurrencyAmount } from '@cronosdex/sdk'
import { useXVaultContract } from '../../hooks/useContract'

import { TransactionResponse } from '@ethersproject/providers'
import { useTransactionAdder } from '../../state/transactions/hooks'
import { LoadingView, SubmittedView } from '../ModalViews'
import { tryParseAmount } from '../../state/swap/hooks'
import { RCRX } from '../../constants'
import { useActiveWeb3React } from '../../hooks'
import { XVaultInfo, XVAULT_CONTRACT_ADDRESS } from '../../state/xVault/hooks'
import ProgressCircles from '../ProgressSteps'
import { ApprovalState, useApproveCallback } from '../../hooks/useApproveCallback'

const InfoSection = styled.div<{ dim: boolean }>`
  display: flex;
  justify-content: space-between;
  padding-right: 20px;
  padding-left: 20px;

  opacity: ${({ dim }) => (dim ? 0.5 : 1)};
`

const ContentWrapper = styled(AutoColumn)`
  width: 100%;
  padding: 1rem;
`

interface XVaultWithdrawModalProps {
  isOpen: boolean
  onDismiss: () => void
  xVaultInfo: XVaultInfo
}

export default function XVaultWithdrawModal({
  isOpen,
  onDismiss,
  xVaultInfo,
}: XVaultWithdrawModalProps) {

    const { account } = useActiveWeb3React()

  // track and parse user input
  const [typedValue, setTypedValue] = useState('')
  const parsedAmount: CurrencyAmount | undefined = tryParseAmount(typedValue, RCRX)




  // state for pending and submitted txn views
  const addTransaction = useTransactionAdder()
  const [attempting, setAttempting] = useState<boolean>(false)
  const [hash, setHash] = useState<string | undefined>()
  const wrappedOnDismiss = useCallback(() => {
    setHash(undefined)
    setAttempting(false)
    onDismiss()
  }, [onDismiss])

  // approval data for stake
  const deadline = useTransactionDeadline()

  // const isArgentWallet = useIsArgentWallet()
  const xVaultContract = useXVaultContract(XVAULT_CONTRACT_ADDRESS)
  async function onWithdraw() {
    setAttempting(true)
    if (xVaultContract && parsedAmount && deadline) {
        xVaultContract.withdraw(`0x${parsedAmount.raw.toString(16)}`)
        .then((response: TransactionResponse) => {
          addTransaction(response, {
            summary: `Withdraw rCRX from xVault`
          })
          setHash(response.hash)
        })
        .catch((error: any) => {
          setAttempting(false)
          console.log(error)
        })
    }
  }

  // wrapped onUserInput to clear signatures
  const onUserInput = useCallback((typedValue: string) => {
    setSignatureData(null)
    setTypedValue(typedValue)
  }, [])


  const maxAmountInput = xVaultInfo?.rCRXBalance
  const atMaxAmount = Boolean(maxAmountInput && parsedAmount?.equalTo(maxAmountInput))
  const handleMax = useCallback(() => {
    maxAmountInput && onUserInput(maxAmountInput.toExact())
  }, [maxAmountInput, onUserInput])


  const [signatureData, setSignatureData] = useState<{ v: number; r: string; s: string; deadline: number } | null>(null)
  const [approval, approveCallback] = useApproveCallback(parsedAmount, XVAULT_CONTRACT_ADDRESS)

  
  async function onAttemptToApprove() {
    return approveCallback()
  }

  let error: string | undefined
  if (!account) {
    error = 'Connect Wallet'
  }
  if (!xVaultInfo?.availableToWithdraw){
    error = error ?? 'Locked!'
  }
  if (!parsedAmount) {
    error = error ?? 'Enter an amount'
  }
  if (parsedAmount && maxAmountInput && parsedAmount.greaterThan(maxAmountInput)){
    error = error ?? 'Insufficient Balance'
  }


  return (
    <Modal isOpen={isOpen} onDismiss={wrappedOnDismiss} maxHeight={90}>
      {!attempting && !hash && (
        <ContentWrapper gap="lg">
          <RowBetween>
            <TYPE.mediumHeader>Withdraw from xVault</TYPE.mediumHeader>
            <CloseIcon onClick={wrappedOnDismiss} />
          </RowBetween>
          <CurrencyInputPanel
            value={typedValue}
            onUserInput={onUserInput}
            onMax={handleMax}
            showMaxButton={!atMaxAmount}
            currency={RCRX}
            hideBalance={true}
            disableCurrencySelect={true}
            myBalanceText={`Available to withdraw: ${xVaultInfo?.rCRXBalance?.toSignificant()} `}
            id="withdraw-rcrx-token"
          />

            { !xVaultInfo?.availableToWithdraw && (
                <InfoSection dim={true}>
                    Locked Until: {xVaultInfo?.lockedUntil?.toUTCString()}
                </InfoSection>
            )}
       

            <RowBetween>
            <ButtonConfirmed
              mr="0.5rem"
              onClick={onAttemptToApprove}
              confirmed={approval === ApprovalState.APPROVED || signatureData !== null}
              disabled={approval !== ApprovalState.NOT_APPROVED || signatureData !== null}
            >
              Approve
            </ButtonConfirmed>
            <ButtonError
              disabled={!!error || (signatureData === null && approval !== ApprovalState.APPROVED)}
              error={!xVaultInfo?.availableToWithdraw || ( !!error && !!parsedAmount)}
              onClick={onWithdraw}
            >
              {error ?? 'Redeem'}
            </ButtonError>
          </RowBetween>
          <ProgressCircles steps={[approval === ApprovalState.APPROVED || signatureData !== null]} disabled={true} />
        </ContentWrapper>
      )}
      {attempting && !hash && (
        <LoadingView onDismiss={wrappedOnDismiss}>
          <AutoColumn gap="12px" justify={'center'}>
            <TYPE.largeHeader>Withdrawing Tokens</TYPE.largeHeader>
            <TYPE.body fontSize={20}>
              {parsedAmount?.toSignificant(4)} xCRX
            </TYPE.body>
          </AutoColumn>
        </LoadingView>
      )}
      {attempting && hash && (
        <SubmittedView onDismiss={wrappedOnDismiss} hash={hash}>
          <AutoColumn gap="12px" justify={'center'}>
            <TYPE.largeHeader>Transaction Submitted</TYPE.largeHeader>
            <TYPE.body fontSize={20}>
              Withdrew {parsedAmount?.toSignificant(4)} xCRX
            </TYPE.body>
          </AutoColumn>
        </SubmittedView>
      )}
    </Modal>
  )
}
